import Image from 'next/image';
import Link from 'next/link';

import { IBlogDictionary } from '@/dictionaries/pages/blog/types';
import { cn } from '@/lib/utils';
import { TranslatedBlogArticle } from '@/types/Blog';
import getMediaFilterUrl from '@/utils/get-media-filter';
import CreateMediaFilterTrigger from '@/components/Media/CreateMediaFilterTrigger';

import { buttonVariants } from '../Button';
import { AnimatedLink } from '../AnimatedLink';
import Paragraph from '../Paragraph';

import './styles.css';

type Props = {
  lang: string;
  dict: IBlogDictionary;
  article: TranslatedBlogArticle;
};

export default function BlogLastArticlesCard({ lang, dict, article }: Props) {
  function stripTagsAndTruncate(text: string) {
    const stripped = text.replace(/(<([^>]+)>)/gi, '');

    return stripped.slice(0, 165) + (165 < stripped.length ? '...' : '');
  }

  function getArticleLink(slug: string) {
    let baseUrl = lang === 'en' ? '' : `/${lang}`;

    return `${baseUrl}/blog/${slug}`;
  }

  const link = getArticleLink(article.blogArticleTranslation.slug);

  const filterName = '1920w_webp';

  const filteredArticleImage = getMediaFilterUrl({
    mediaFilters: article?.image?.mediaFilters,
    filterName,
  });

  const reportNoImg = () => {
    console.log('[CAROUSEL CARD] No image path on image');
    return null;
  };

  return (
    <div className="fa-card">
      <CreateMediaFilterTrigger
        imageUrl={filteredArticleImage}
        mediaId={article?.image?.id}
        filterName={filterName}
      />
      <div className="fa-card-image-container">
        {article.image && article.image.path ? (
          <Image
            src={
              filteredArticleImage ??
              `${process.env.NEXT_PUBLIC_S3_URI}/thumbs/${filterName}/${article.image.path}${article.image.filename}`
            }
            alt={article.blogArticleTranslation.title}
            width={100}
            height={100}
            quality={85}
            className="align-middle max-w-full absolute top-0 bottom-0 left-1/2 object-cover w-auto h-full -translate-x-1/2 desktop:transform-none desktop:w-full desktop:h-full desktop:left-0"
            sizes="(max-width: 480px) 100vw, (max-width: 767px) 90vw, (max-width: 1280px) 80vw, 100vw"
            loading="lazy"
          />
        ) : (
          reportNoImg()
        )}
      </div>
      <div className="fa-card-content-container">
        <h3 className="text-xl leading-[1.25] text-white font-semibold mb-[20px] z-10 desktop:text-4xl desktop:text-graphite">
          {article.blogArticleTranslation.title}
        </h3>
        <div className="description-container">
          <Paragraph
            content={stripTagsAndTruncate(
              article.blogArticleTranslation.abstract || '',
            )}
          />
        </div>

        <Link
          className={cn(
            buttonVariants({ variant: 'default', size: 'default' }),
            'z-10 py-[20px] px-[10px] mt-[30px] desktop:hidden',
          )}
          href={link}>
          {dict.readMore}
        </Link>
        <AnimatedLink
          href={link}
          className="w-fit hidden desktop:block py-[20px] px-[10px] mt-[30px] desktop:px-[40px] desktop:w-fit !text-sm">
          {dict.readMore}
        </AnimatedLink>
      </div>
    </div>
  );
}
